// src/prefabsComponents/Sidenav/index.jsx
import { useState, useRef, useEffect, useContext } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Icon from '@mui/material/Icon';
import Drawer from '@mui/material/Drawer';

// Componentes propios
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SidenavCollapse from 'prefabsComponents/Sidenav/SidenavCollapse';
import SidenavList from 'prefabsComponents/Sidenav/SidenavList';
import SidenavItem from 'prefabsComponents/Sidenav/SidenavItem';
import SidenavRoot from 'prefabsComponents/Sidenav/SidenavRoot';
import sidenavLogoLabel from 'prefabsComponents/Sidenav/styles/sidenav';
import { navbarDesktopMenu } from 'prefabsComponents/Navbars/DashboardNavbar/styles';

// Contextos
import { useSoftUIController, setMiniSidenav } from 'context';
import { AuthContext } from 'context/AuthContext';

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  // Estado global y ruta actual
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split('/')[1];
  const itemName = pathname.split('/')[2];

  // Auth
  const { user, allowedPath } = useContext(AuthContext);

  // Normaliza a "/..."
  const normalize = (p = '') => (p?.startsWith('/') ? p : `/${p || ''}`);

  // Filtro central: visible, roles y whitelist (para client)
  const filterMenu = (items = []) =>
    items.reduce((acc, it) => {
      if (!it) return acc;

      // 1) visible
      if (it.visible === false) return acc;

      // 2) roles explícitos
      if (Array.isArray(it.roles) && it.roles.length) {
        if (!user || !it.roles.includes(user.role)) return acc;
      }

      // 3) grupos (collapse) -> filtra hijos
      if (Array.isArray(it.collapse)) {
        const children = filterMenu(it.collapse);
        if (children.length === 0) return acc; // oculta grupos vacíos
        acc.push({ ...it, collapse: children });
        return acc;
      }

      // 4) item con route interno -> respetar whitelist client
      if (it.route && !it.href) {
        const path = normalize(it.route);
        if (!allowedPath(path)) return acc;
      }

      acc.push(it);
      return acc;
    }, []);

  // Forzar sidenav mini en desktop al montar
  useEffect(() => {
    setMiniSidenav(dispatch, true);
  }, [dispatch]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // En móvil nunca mini
  const effectiveMiniSidenav = isMobile ? false : miniSidenav;

  // Estados de UI (colapsables)
  const [openCollapse, setOpenCollapse] = useState(null);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(null);
  const collapseTimerRef = useRef(null);
  const nestedTimerRef = useRef(null);
  const [hoverExpanded, setHoverExpanded] = useState(false);

  // Drawer móvil
  const [mobileSidenavOpen, setMobileSidenavOpen] = useState(false);
  const toggleMobileSidenav = () => setMobileSidenavOpen((prev) => !prev);

  // Hover expand/contract (solo desktop)
  const handleSidenavMouseEnter = () => {
    if (!isMobile && miniSidenav) {
      setMiniSidenav(dispatch, false);
      setHoverExpanded(true);
    }
  };
  const handleSidenavMouseLeave = () => {
    if (!isMobile && hoverExpanded) {
      setMiniSidenav(dispatch, true);
      setHoverExpanded(false);
    }
  };

  // Handlers collapse principal
  const handleCollapseMouseEnter = (key) => {
    if (!isMobile) {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
      setOpenCollapse(key);
    }
  };
  const handleCollapseMouseLeave = () => {
    if (!isMobile) {
      collapseTimerRef.current = setTimeout(() => {
        setOpenCollapse(null);
      }, 200);
    }
  };
  const handleCollapseClick = (key) => {
    if (isMobile) {
      setOpenCollapse((current) => (current === key ? null : key));
    }
  };

  // Handlers nested
  const handleNestedMouseEnter = (name) => {
    if (!isMobile) {
      if (nestedTimerRef.current) {
        clearTimeout(nestedTimerRef.current);
        nestedTimerRef.current = null;
      }
      setOpenNestedCollapse(name);
    }
  };
  const handleNestedMouseLeave = () => {
    if (!isMobile) {
      nestedTimerRef.current = setTimeout(() => {
        setOpenNestedCollapse(null);
      }, 200);
    }
  };
  const handleNestedClick = (name) => {
    if (isMobile) {
      setOpenNestedCollapse((current) => (current === name ? null : name));
    }
  };

  // Submenús (nested)
  const renderNestedCollapse = (collapse) =>
    (collapse || [])
      .filter((item) => item.visible !== false)
      .map(({ name, route, key, href }) =>
        href ? (
          <Link key={key} href={href} target="_blank" rel="noreferrer" sx={{ textDecoration: 'none' }}>
            <SoftBox
              onMouseEnter={() => !isMobile && handleNestedMouseEnter(name)}
              onMouseLeave={() => !isMobile && handleNestedMouseLeave()}
              onClick={() => isMobile && handleNestedClick(name)}
            >
              <SidenavItem name={name} nested open={openNestedCollapse === name} />
            </SoftBox>
          </Link>
        ) : (
          <NavLink to={route} key={key} style={{ textDecoration: 'none' }}>
            <SoftBox
              onMouseEnter={() => !isMobile && handleNestedMouseEnter(name)}
              onMouseLeave={() => !isMobile && handleNestedMouseLeave()}
              onClick={() => isMobile && handleNestedClick(name)}
            >
              <SidenavItem
                name={name}
                active={route === pathname}
                nested
                open={openNestedCollapse === name}
              />
            </SoftBox>
          </NavLink>
        )
      );

  // Ítems principales (collapse)
  const renderCollapse = (collapses) =>
    (collapses || [])
      .filter((item) => item.visible !== false)
      .map(({ name, collapse, route, href, key }) => {
        let returnValue;
        if (collapse) {
          returnValue = (
            <SoftBox
              key={key}
              onMouseEnter={() => !isMobile && handleNestedMouseEnter(name)}
              onMouseLeave={() => !isMobile && handleNestedMouseLeave()}
              onClick={() => isMobile && handleNestedClick(name)}
            >
              <SidenavItem name={name} active={key === itemName} open={openNestedCollapse === name}>
                {renderNestedCollapse(collapse)}
              </SidenavItem>
            </SoftBox>
          );
        } else {
          returnValue = href ? (
            <Link
              key={key}
              href={href}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: 'none' }}
            >
              <SidenavItem
                name={name}
                active={key === itemName}
                nested
                open={openNestedCollapse === name}
              />
            </Link>
          ) : (
            <NavLink to={route} key={key} style={{ textDecoration: 'none' }}>
              <SoftBox>
                <SidenavItem
                  name={name}
                  active={route === pathname}
                  nested
                  open={openNestedCollapse === name}
                />
              </SoftBox>
            </NavLink>
          );
        }
        return <SidenavList key={key}>{returnValue}</SidenavList>;
      });

  // Aplicar filtro por roles/whitelist al árbol completo
  const filteredRoutes = filterMenu(routes);

  // Render de todas las rutas filtradas
  const renderRoutes = filteredRoutes.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route }) => {
      let returnValue;
      if (type === 'collapse') {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: 'none' }}
            >
              <SoftBox
                onMouseEnter={() => !isMobile && handleCollapseMouseEnter(key)}
                onMouseLeave={() => !isMobile && handleCollapseMouseLeave()}
                onClick={() => isMobile && handleCollapseClick(key)}
              >
                <SidenavCollapse
                  name={name}
                  icon={icon}
                  active={key === collapseName}
                  noCollapse={noCollapse}
                  open={openCollapse === key}
                />
              </SoftBox>
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key} style={{ textDecoration: 'none' }}>
              <SoftBox
                onMouseEnter={() => !isMobile && handleCollapseMouseEnter(key)}
                onMouseLeave={() => !isMobile && handleCollapseMouseLeave()}
                onClick={() => isMobile && handleCollapseClick(key)}
              >
                <SidenavCollapse
                  name={name}
                  icon={icon}
                  noCollapse={noCollapse}
                  active={key === collapseName}
                  open={openCollapse === key}
                >
                  {collapse ? renderCollapse(collapse) : null}
                </SidenavCollapse>
              </SoftBox>
            </NavLink>
          );
        } else {
          returnValue = (
            <SoftBox
              key={key}
              onMouseEnter={() => !isMobile && handleCollapseMouseEnter(key)}
              onMouseLeave={() => !isMobile && handleCollapseMouseLeave()}
              onClick={() => isMobile && handleCollapseClick(key)}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                open={openCollapse === key}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </SoftBox>
          );
        }
      } else if (type === 'title') {
        returnValue = (
          <SoftTypography
            key={key}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            opacity={0.6}
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </SoftTypography>
        );
      } else if (type === 'divider') {
        returnValue = <Divider key={key} />;
      }
      return returnValue;
    }
  );

  // Contenido del Sidenav
  const sidenavContent = (
    <SoftBox display="flex" flexDirection="column" justifyContent="space-between" height="100%">
      <SoftBox>
        <SoftBox pt={3} pb={1} px={4} textAlign="center" position="relative">
          {isMobile && (
            <SoftBox
              position="absolute"
              top={0}
              right={0}
              p={1.625}
              sx={{ cursor: 'pointer' }}
              onClick={() => setMobileSidenavOpen(false)}
            >
              <SoftTypography variant="h6" color="secondary">
                <Icon sx={{ fontWeight: 'bold' }}>close</Icon>
              </SoftTypography>
            </SoftBox>
          )}
          <SoftBox component={NavLink} to="/" display="flex" alignItems="center">
            {brand && <SoftBox component="img" src={brand} alt="Logo" width="4rem" />}
            <SoftBox
              width={!brandName && '100%'}
              sx={(theme) => sidenavLogoLabel(theme, { miniSidenav: effectiveMiniSidenav })}
            >
              <SoftTypography component="h6" variant="button" fontWeight="medium">
                {brandName}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>
        <Divider />
        <List>{renderRoutes}</List>
      </SoftBox>
    </SoftBox>
  );

  return (
    <>
      {isMobile && (
        <SoftBox display="block" position="fixed" top="1rem" right="1rem" zIndex={9999}>
          <Icon onClick={toggleMobileSidenav} sx={{ cursor: 'pointer', fontSize: '2rem', color: '#3a416f' }}>
            menu
          </Icon>
        </SoftBox>
      )}
      {isMobile ? (
        <Drawer open={mobileSidenavOpen} onClose={toggleMobileSidenav} ModalProps={{ keepMounted: true }}>
          {sidenavContent}
        </Drawer>
      ) : (
        <SidenavRoot
          {...rest}
          variant="permanent"
          ownerState={{ transparentSidenav, miniSidenav }}
          onMouseEnter={handleSidenavMouseEnter}
          onMouseLeave={handleSidenavMouseLeave}
          sx={{ display: { xs: 'block', md: 'block' }, zIndex: { xs: 9999, md: 'auto' } }}
        >
          {sidenavContent}
        </SidenavRoot>
      )}
    </>
  );
}

Sidenav.defaultProps = {
  color: 'info',
  brand: '',
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark']),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
